import { gaeApp } from '../components/helpers/Analytics';
import { getResponseErrorMessage } from '../server/api/PaperflyAPI';
import axios from 'axios';

export const putFileBySignedUrl = async function(
  signedUrl,
  fileType,
  fileData
) {
  let options = {
    headers: {
      'Content-Type': fileType,
      'Cache-Control': 'public, max-age=31536000, immutable',
      'x-amz-acl': 'bucket-owner-full-control'
    },
    crossDomain: true
  };
  return new Promise((resolve, reject) => {
    axios.put(signedUrl, fileData, options).then(
      answer => {
        if (answer.statusText !== 'OK') {
          reject({
            success: false,
            errorMessage: 'Error on upload file by signedURL, status is not OK',
            responseError: answer
          });
        } else {
          resolve({
            success: true
          });
        }
      },
      error => {
        reject({
          success: false,
          errorMessage: 'Error on upload file by signedURL',
          responseError: error
        });
      }
    );
  });
};

export const getBase64FromUrl = async function(url) {
  const blob = await createBlobFromUrl(url);
  if (!blob) return null;
  // noinspection UnnecessaryLocalVariableJS
  return blob2base64(blob);
};

const JPEG_MAGIC = [0xff, 0xd8, 0xff];
const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47];

const startsWithBytes = (bytes, magic) =>
  magic.every((byte, index) => bytes[index] === byte);

/**
 * Parse a data-URL or raw base64 string and check JPEG/PNG magic bytes.
 * Returns { valid, mime, reason, byteLength } for logging / early fail.
 */
export const analyzeJpegOrPngBase64 = function(base64) {
  if (!base64 || typeof base64 !== 'string') {
    return { valid: false, mime: null, reason: 'EMPTY', byteLength: 0 };
  }

  let mime = null;
  let payload = base64.trim();

  if (payload.indexOf('data:') === 0) {
    const commaIndex = payload.indexOf(',');
    if (commaIndex === -1) {
      return {
        valid: false,
        mime: null,
        reason: 'MALFORMED_DATA_URL',
        byteLength: 0
      };
    }
    const header = payload.substring(0, commaIndex);
    mime = (header.match(/^data:([^;,]*)/) || [])[1] || null;
    if (header.indexOf(';base64') === -1) {
      return {
        valid: false,
        mime,
        reason: 'DATA_URL_NOT_BASE64',
        byteLength: 0
      };
    }
    payload = payload.substring(commaIndex + 1);
  }

  if (
    mime &&
    mime !== 'image/jpeg' &&
    mime !== 'image/png' &&
    mime !== 'image/jpg'
  ) {
    return {
      valid: false,
      mime,
      reason: 'UNSUPPORTED_MIME',
      byteLength: 0
    };
  }

  let binary;
  try {
    binary = atob(payload.replace(/\s/g, ''));
  } catch (e) {
    return {
      valid: false,
      mime,
      reason: 'INVALID_BASE64',
      byteLength: 0
    };
  }

  if (binary.length < 4) {
    return {
      valid: false,
      mime,
      reason: 'TOO_SHORT',
      byteLength: binary.length
    };
  }

  const bytes = [
    binary.charCodeAt(0),
    binary.charCodeAt(1),
    binary.charCodeAt(2),
    binary.charCodeAt(3)
  ];

  if (startsWithBytes(bytes, JPEG_MAGIC)) {
    return {
      valid: true,
      mime: mime || 'image/jpeg',
      reason: null,
      byteLength: binary.length
    };
  }
  if (startsWithBytes(bytes, PNG_MAGIC)) {
    return {
      valid: true,
      mime: mime || 'image/png',
      reason: null,
      byteLength: binary.length
    };
  }

  return {
    valid: false,
    mime,
    reason: 'NOT_JPEG_OR_PNG',
    byteLength: binary.length,
    headerHex: bytes.map(b => b.toString(16).padStart(2, '0')).join(' ')
  };
};

export const isValidJpegOrPngBase64 = function(base64) {
  return analyzeJpegOrPngBase64(base64).valid;
};

export const extBase642LocalURL = async function(url) {
  const base64 = await createTextFromUrl(url);
  return base64 ? window.URL.createObjectURL(base64ToBlob(base64)) : null;
};

export const createBlobFromUrl = async function(url) {
  const response = await fetchImageUrl(url);
  return response ? response.blob() : null;
};

export const base64ToBlob = function(base64) {
  // convert base64/URLEncoded data component to raw binary data held in a string
  let byteString;
  if (base64.split(',')[0].indexOf('base64') >= 0)
    byteString = atob(base64.split(',')[1]);
  else byteString = unescape(base64.split(',')[1]);

  // separate out the mime component
  const mimeString = base64
    .split(',')[0]
    .split(':')[1]
    .split(';')[0];

  // write the bytes of the string to a typed array
  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new Blob([ia], { type: mimeString });
};

export const createTextFromUrl = async function(url) {
  const response = await fetchImageUrl(url);
  return response ? response.text() : null;
};

const CROSS_DEVICE_PAGE_ID_PLACEHOLDER = 'PAGE_ID_PLACEHOLDER';

const getCrossDevicePageDownloadUrl = (downloadUrl, pageId) =>
  downloadUrl.replace(CROSS_DEVICE_PAGE_ID_PLACEHOLDER, pageId);

export const isCrossDevicePageImageAvailable = async (downloadUrl, pageId) => {
  const base64 = await createTextFromUrl(
    getCrossDevicePageDownloadUrl(downloadUrl, pageId)
  );
  return Boolean(base64);
};

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export const waitForCrossDeviceImagesAvailable = async (
  pageIds,
  downloadUrl,
  { maxAttempts = 15, delayMs = 500 } = {}
) => {
  if (!pageIds?.length || !downloadUrl) {
    return true;
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const availability = await Promise.all(
      pageIds.map(pageId =>
        isCrossDevicePageImageAvailable(downloadUrl, pageId)
      )
    );

    if (availability.every(Boolean)) {
      return true;
    }

    if (attempt < maxAttempts) {
      await delay(delayMs);
    }
  }

  return false;
};

export const fetchImageUrl = async function(url) {
  if (!url) return null;

  let response = null;

  await fetch(url, {
    method: 'GET', // *GET, POST, PUT, DELETE, etc.
    mode: 'cors', // no-cors, *cors, same-origin
    cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
    headers: {
      'Content-Type': 'image/jpeg',
      'Access-Control-Allow-Origin': '*'
    }
  })
    .then(r => {
      if (r.status === 200) {
        response = r;
      } else {
        gaeApp(
          'FETCH_FROM_URL_ERROR',
          'WRONG_RESPONSE_STATUS',
          getResponseErrorMessage(r)
        );
      }
    })
    .catch(error => {
      gaeApp('FETCH_FROM_URL_ERROR', error?.message);
      console.error('Error on fetch url:', error);
    });
  return response;
};

const uint8ToBinaryString = bytes => {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return binary;
};

/**
 * Data-URL from blob bytes. Does not use FileReader: Safari/WebKit can throw
 * "Can't find variable: FileReader" after the blob is already loaded.
 * Output shape matches readAsDataURL: data:<mime>;base64,...
 */
export const blob2base64 = async function(blob) {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const mime = blob.type || '';
  return `data:${mime};base64,${btoa(uint8ToBinaryString(bytes))}`;
};

export const getImageSize = function(url, onComplete) {
  const img = new Image();
  img.onload = function() {
    onComplete({
      width: img.naturalWidth,
      height: img.naturalHeight
    });
  };

  img.src = url;
};

/**
 * Export a canvas as JPEG after compositing onto white.
 * JPEG has no alpha; browsers treat transparent pixels as black, which hides
 * black ink on transparent PNGs (signatures / scanned overlays).
 * Does not mutate the source canvas.
 */
export const canvasToJpegDataUrl = function(canvas, quality = 1) {
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = canvas.width;
  exportCanvas.height = canvas.height;
  const ctx = exportCanvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
  ctx.drawImage(canvas, 0, 0);
  return exportCanvas.toDataURL('image/jpeg', quality);
};

export function downloadURI(url, filename) {
  return fetch(url)
    .then(response => response.blob())
    .then(blob => {
      const startDownload = blobURL => {
        const a = document.createElement('a');
        a.href = blobURL;
        a.download = filename || 'info_document.pdf';
        a.target = '_blank';
        a.classList.add('external');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      };

      if (/iPad|iPhone|iPod|Macintosh|Mac/.test(navigator.userAgent)) {
        blob2base64(blob).then(base64 => {
          const blobURL = base64.replace(
            'data:application/pdf;',
            'data:application/octet-stream;'
          );
          startDownload(blobURL);
        });
      } else {
        const blobURL = URL.createObjectURL(blob);
        startDownload(blobURL);
        URL.revokeObjectURL(blobURL);
      }
    });
}
